<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >System status</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">System status</h1>
        <p class="page-head__subtitle">
          Live health checks of platform dependencies. Each check runs when this page loads.
        </p>
      </div>
      <div class="page-head__actions">
        @if (systemStatus(); as s) {
          <span
            class="status-pill"
            [class.status-pill--ok]="s.overall === 'ok'"
            [class.status-pill--degraded]="s.overall === 'degraded'"
            [class.status-pill--error]="s.overall === 'error'"
          >
            {{ s.overall === 'ok' ? 'Healthy' : s.overall === 'degraded' ? 'Degraded' : 'Error' }}
          </span>
        }
        <button
          mat-stroked-button
          type="button"
          (click)="loadSystemStatus()"
          [disabled]="statusLoading()"
        >
          Re-check
        </button>
      </div>
    </div>
  </header>

  <!-- Developer docs entry point -->
  <a class="docs-link-card" routerLink="/admin/developer-docs">
    <mat-card appearance="outlined" class="docs-link-card__card">
      <mat-card-content class="docs-link-card__content">
        <span class="docs-link-card__icon" aria-hidden="true">
          <app-icon name="book" [size]="24" />
        </span>
        <span class="docs-link-card__text">
          <span class="docs-link-card__title">Developer environment docs</span>
          <span class="docs-link-card__subtitle">
            Hand-over reference — what's real vs. faked locally (Mailpit, S3, Claude), the Debug
            menu, and how each check works.
          </span>
        </span>
        <span class="docs-link-card__chevron" aria-hidden="true">
          <app-icon name="arrow-right" [size]="20" />
        </span>
      </mat-card-content>
    </mat-card>
  </a>

  <!-- System logs entry point -->
  <a class="docs-link-card" routerLink="/admin/system-status/logs">
    <mat-card appearance="outlined" class="docs-link-card__card">
      <mat-card-content class="docs-link-card__content">
        <span class="docs-link-card__icon" aria-hidden="true">
          <app-icon name="file-text" [size]="24" />
        </span>
        <span class="docs-link-card__text">
          <span class="docs-link-card__title">System logs</span>
          <span class="docs-link-card__subtitle">
            Browse recent WARNING and ERROR log entries, paginated, to help debug issues.
          </span>
        </span>
        <span class="docs-link-card__chevron" aria-hidden="true">
          <app-icon name="arrow-right" [size]="20" />
        </span>
      </mat-card-content>
    </mat-card>
  </a>

  <!-- Database browser entry point -->
  <a class="docs-link-card" routerLink="/admin/system-status/database">
    <mat-card appearance="outlined" class="docs-link-card__card">
      <mat-card-content class="docs-link-card__content">
        <span class="docs-link-card__icon" aria-hidden="true">
          <app-icon name="database" [size]="24" />
        </span>
        <span class="docs-link-card__text">
          <span class="docs-link-card__title">Database browser</span>
          <span class="docs-link-card__subtitle">
            Browse application tables and their data — search across text columns and sort by any
            column. Read-only.
          </span>
        </span>
        <span class="docs-link-card__chevron" aria-hidden="true">
          <app-icon name="arrow-right" [size]="20" />
        </span>
      </mat-card-content>
    </mat-card>
  </a>

  @if (statusLoading()) {
    <div class="status-list" aria-label="Loading…" aria-busy="true">
      @for (row of [1, 2, 3, 4]; track row) {
        <mat-card appearance="outlined" class="status-card">
          <mat-card-content>
            <div class="status-card__head">
              <span class="sg-skeleton status-card__skeleton-dot" aria-hidden="true"></span>
              <span class="sg-skeleton sg-skeleton--text status-card__skeleton-label"></span>
              <span class="sg-skeleton sg-skeleton--chip"></span>
            </div>
            <span class="sg-skeleton sg-skeleton--text status-card__skeleton-detail"></span>
          </mat-card-content>
        </mat-card>
      }
    </div>
  } @else if (statusError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load system checks</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="loadSystemStatus()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (systemStatus(); as s) {
    <div class="status-list">
      @for (check of s.checks; track check.key) {
        <mat-card appearance="outlined" class="status-card">
          <mat-card-content>
            <div class="status-card__head">
              <span
                class="status-dot"
                [class.status-dot--ok]="check.status === 'ok'"
                [class.status-dot--degraded]="check.status === 'degraded'"
                [class.status-dot--error]="check.status === 'error'"
                [class.status-dot--skipped]="check.status === 'skipped'"
                aria-hidden="true"
              ></span>
              <p class="status-card__label">{{ check.label }}</p>
              <app-status-chip [status]="check.status" />
            </div>
            <p class="status-card__detail">{{ check.detail }}</p>
          </mat-card-content>
        </mat-card>
      }
    </div>
  }

  <!-- Claude API connection test -->
  @if (systemStatus()?.configuration?.length) {
    <section class="claude-test" aria-labelledby="configuration-heading">
      <div class="claude-test__intro">
        <h2 id="configuration-heading" class="claude-test__title">Environment settings</h2>
        <p class="claude-test__subtitle">
          What this environment is running on. Only these named settings are shown, never a key,
          password or connection string.
        </p>
      </div>
      <mat-card appearance="outlined" class="claude-test__card">
        <mat-card-content>
          <dl class="config-list" data-testid="system-configuration">
            @for (row of systemStatus()!.configuration; track row.key) {
              <div class="config-list__row">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </div>
            }
          </dl>
        </mat-card-content>
      </mat-card>
    </section>
  }

  <section class="claude-test" aria-labelledby="claude-test-heading">
    <div class="claude-test__intro">
      <h2 id="claude-test-heading" class="claude-test__title">Test the Claude API connection</h2>
      <p class="claude-test__subtitle">
        Send a minimal live request to the Anthropic API using the configured key and evaluation
        model. This makes a real, billable call — it runs only when you click the button, not on
        page load.
      </p>
    </div>

    <mat-card appearance="outlined" class="claude-test__card">
      <mat-card-content>
        <button
          mat-flat-button
          color="primary"
          type="button"
          class="claude-test__submit"
          (click)="testClaudeConnection()"
          [disabled]="claudeTesting()"
        >
          @if (claudeTesting()) {
            <mat-spinner diameter="18" />
            Testing…
          } @else {
            Test connection
          }
        </button>

        @if (claudeResult(); as r) {
          <div
            class="claude-test__result"
            [class.claude-test__result--ok]="r.status === 'ok'"
            [class.claude-test__result--error]="r.status === 'error'"
            [class.claude-test__result--skipped]="r.status === 'skipped'"
            [attr.role]="r.status === 'error' ? 'alert' : 'status'"
          >
            <p class="claude-test__result-line">
              <span class="claude-test__result-badge">{{ r.status }}</span>
              {{ r.detail }}
            </p>
            @if (r.status === 'ok' && r.meta) {
              <p class="claude-test__result-meta">
                Model <code>{{ r.meta.response_model || r.meta.model }}</code>
                @if (r.meta.input_tokens !== null && r.meta.output_tokens !== null) {
                  · {{ r.meta.input_tokens }} in / {{ r.meta.output_tokens }} out tokens
                }
              </p>
            }
          </div>
        } @else if (claudeError(); as e) {
          <div class="claude-test__result claude-test__result--error" role="alert">
            <p class="claude-test__result-line">{{ e }}</p>
          </div>
        }
      </mat-card-content>
    </mat-card>
  </section>

  <!-- Seed demo data (FLOW-181) -->
  <section class="claude-test" aria-labelledby="near-test-heading">
    <div class="claude-test__intro">
      <h2 id="near-test-heading" class="claude-test__title">Test the NEAR AI connection</h2>
      <p class="claude-test__subtitle">
        Send a one-word live request to a NEAR AI model and ask where it runs. No client data is
        sent. This makes a real, billable call, and only when you click the button.
      </p>
    </div>

    <mat-card appearance="outlined" class="claude-test__card">
      <mat-card-content>
        <button
          mat-flat-button
          color="primary"
          type="button"
          class="claude-test__submit"
          (click)="testNearConnection()"
          [disabled]="nearTesting()"
        >
          @if (nearTesting()) {
            <mat-spinner diameter="18" />
            Testing…
          } @else {
            Test connection
          }
        </button>

        @if (nearResult(); as r) {
          <div
            class="claude-test__result"
            [class.claude-test__result--ok]="r.status === 'ok'"
            [class.claude-test__result--error]="r.status === 'error'"
            [class.claude-test__result--skipped]="r.status === 'skipped'"
            [attr.role]="r.status === 'error' ? 'alert' : 'status'"
          >
            <p class="claude-test__result-line">
              <span class="claude-test__result-badge">{{ r.status }}</span>
              {{ r.detail }}
            </p>
            @if (r.status === 'ok') {
              <p class="claude-test__result-meta">
                Model <code>{{ r.meta.response_model || r.meta.model }}</code> ·
                {{ r.meta.input_tokens }} in / {{ r.meta.output_tokens }} out tokens
              </p>
            }
            @if (r.meta.attestation; as a) {
              <p class="claude-test__result-meta">
                @if (a.verified) {
                  Enclave verified: the gateway and {{ a.model_enclaves?.length }} model enclave(s)
                  passed Intel's and NVIDIA's checks against a fresh nonce.
                } @else {
                  Enclave not verified, so NEAR AI would be refused client documents: {{ a.error }}
                }
              </p>
            }
          </div>
        } @else if (nearError(); as e) {
          <div class="claude-test__result claude-test__result--error" role="alert">
            <p class="claude-test__result-line">{{ e }}</p>
          </div>
        }
      </mat-card-content>
    </mat-card>
  </section>

  <section class="claude-test" aria-labelledby="seed-demo-heading">
    <div class="claude-test__intro">
      <h2 id="seed-demo-heading" class="claude-test__title">Seed demo data</h2>
      <p class="claude-test__subtitle">
        Populate this environment with demo clients, applications, facilities and documents so there
        is something to click through. Nothing existing is deleted. Uploaded documents trigger a
        live, billable Claude check when document assessment is switched on.
      </p>
    </div>

    <mat-card appearance="outlined" class="claude-test__card">
      <mat-card-content>
        <button
          mat-flat-button
          color="primary"
          type="button"
          class="claude-test__submit"
          (click)="seedDemoData()"
          [disabled]="seeding()"
        >
          @if (seeding()) {
            <mat-spinner diameter="18" />
            Seeding…
          } @else {
            Seed demo data
          }
        </button>

        @if (seedResult(); as r) {
          <div
            class="claude-test__result"
            [class.claude-test__result--ok]="r.status === 'ok'"
            [class.claude-test__result--error]="r.status === 'error'"
            [attr.role]="r.status === 'error' ? 'alert' : 'status'"
          >
            <p class="claude-test__result-line">
              <span class="claude-test__result-badge">{{ r.status }}</span>
              {{ r.detail }}
            </p>
            @if (r.status === 'ok' && r.meta.email) {
              <p class="claude-test__result-meta">
                Sign in as <code>{{ r.meta.email }}</code> with password
                <code>{{ r.meta.password }}</code>
              </p>
            }
            @if (r.meta.log?.length) {
              <ul class="claude-test__result-log">
                @for (line of r.meta.log; track $index) {
                  <li>{{ line }}</li>
                }
              </ul>
            }
          </div>
        }
      </mat-card-content>
    </mat-card>
  </section>

  <!-- Test email -->
  <section class="test-email" aria-labelledby="test-email-heading">
    <div class="test-email__intro">
      <h2 id="test-email-heading" class="test-email__title">Send a test email</h2>
      <p class="test-email__subtitle">
        Render a transactional template with sample data and send it through the configured email
        backend — to confirm delivery works and to preview how each template looks.
      </p>
    </div>

    <mat-card appearance="outlined" class="test-email__card">
      <mat-card-content>
        <form [formGroup]="emailForm" (ngSubmit)="sendTestEmail()" class="test-email__form">
          <mat-form-field appearance="outline" class="test-email__field">
            <mat-label>Recipient</mat-label>
            <input matInput type="email" formControlName="recipient" autocomplete="off" />
            @if (
              emailForm.controls.recipient.hasError('required') &&
              emailForm.controls.recipient.touched
            ) {
              <mat-error>A recipient address is required.</mat-error>
            } @else if (emailForm.controls.recipient.hasError('email')) {
              <mat-error>Enter a valid email address.</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="test-email__field">
            <mat-label>Template</mat-label>
            <mat-select formControlName="template">
              @for (t of templates(); track t.key) {
                <mat-option [value]="t.key">{{ t.label }}</mat-option>
              }
            </mat-select>
            @if (
              emailForm.controls.template.hasError('required') &&
              emailForm.controls.template.touched
            ) {
              <mat-error>Choose a template to send.</mat-error>
            }
          </mat-form-field>

          <button
            mat-flat-button
            color="primary"
            type="submit"
            class="test-email__submit"
            [disabled]="sending() || templates().length === 0"
          >
            @if (sending()) {
              <mat-spinner diameter="18" />
              Sending…
            } @else {
              Send test email
            }
          </button>
        </form>

        @if (sendResult(); as r) {
          <div class="test-email__result test-email__result--ok" role="status">
            <p class="test-email__result-line">
              Sent <strong>{{ r.label }}</strong> to <strong>{{ r.recipient }}</strong
              >.
            </p>
            <p class="test-email__result-meta">
              From {{ r.from_email }} · backend <code>{{ r.backend }}</code>
            </p>
          </div>
        } @else if (sendError(); as e) {
          <div class="test-email__result test-email__result--error" role="alert">
            <p class="test-email__result-line">{{ e }}</p>
          </div>
        }
      </mat-card-content>
    </mat-card>
  </section>
</div>
